// Esportati per essere riusati tal quali da assets/js/servizio-richiesta.js
// (modulo di richiesta sempre aperto del singolo servizio) e da
// assets/js/contatti-pagina.js (modulo della Pagina Contatti), che validano
// un insieme di campi diverso ma vogliono lo stesso pattern nome/email/
// azienda/telefono/città, lo stesso messaggino di stato e lo stesso stato
// visivo dei campi di questo form.
export const NAME_PATTERN = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,150}$/;
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const MESSAGE_MIN_LENGTH = 5;
const SUBJECT_MIN_LENGTH = 2;

// Azienda/telefono/città sono sempre facoltativi ovunque compaiono (modulo
// del singolo servizio, dove solo azienda è invece obbligatoria — vedi la
// validazione dedicata in servizio-richiesta.js — e Pagina Contatti, dove
// tutti e tre lo sono): questi pattern si applicano solo se il campo è stato
// compilato, mai a un campo vuoto.
export const AZIENDA_PATTERN = /^[A-Za-z0-9À-ÖØ-öø-ÿ\s.,'&-]{2,150}$/;
export const TELEFONO_PATTERN = /^[0-9+()\-\s]{6,30}$/;
export const CITTA_PATTERN = /^[A-Za-zÀ-ÖØ-öø-ÿ\s.,'-]{2,150}$/;

export function setMessage(el, text, status) {
    el.classList.remove("is-success", "is-error");
    el.innerHTML = "";

    if (!text) {
        return;
    }

    if (status === "success") {
        el.classList.add("is-success");
        el.innerHTML =
            '<svg class="contatti__check" viewBox="0 0 52 52" width="20" height="20" aria-hidden="true" focusable="false">' +
            '<circle class="contatti__check-circle" cx="26" cy="26" r="24" fill="none"/>' +
            '<path class="contatti__check-mark" fill="none" d="M14 27l7 7 16-16"/>' +
            "</svg>" +
            '<span class="contatti__message-text"></span>';
        el.querySelector(".contatti__message-text").textContent = text;
        return;
    }

    if (status === "error") {
        el.classList.add("is-error");
    }

    el.textContent = text;
}

// Aggiorna bordo del campo + aria-invalid, e mostra/nasconde il messaggino che
// spiega come correggere l'errore, una volta che il campo è stato "touched".
// Il messaggio resta collegato al campo via aria-describedby (statico, nel
// markup) anche da nascosto: uno screen reader lo legge solo quando diventa
// visibile, ma l'associazione non va ricreata a runtime.
export function setFieldState(form, name, control, isValid, isTouched) {
    const invalid = isTouched && !isValid;

    if (control) {
        control.classList.toggle("is-invalid", invalid);
        control.classList.toggle("is-valid", isTouched && isValid);
        control.setAttribute("aria-invalid", invalid ? "true" : "false");
    }

    const errorEl = form.querySelector(`[data-error-for="${name}"]`);
    if (errorEl) {
        errorEl.classList.toggle("is-visible", invalid);
    }
}

// Listbox ARIA custom (un <select> nativo non è stilizzabile in modo coerente
// col resto del form): il focus reale resta sempre sul bottone toggle, che
// pilota l'opzione "attiva" tramite aria-activedescendant, sul modello del
// pattern APG "Select Only Combobox". Le singole opzioni non sono nel tab
// order: si spostano con le frecce, non con Tab.
function initContattiSelect(wrapper, onInteract) {
    const toggle = wrapper.querySelector("[data-select-toggle]");
    const optionsList = wrapper.querySelector("[data-select-options]");
    const label = wrapper.querySelector("[data-select-label]");
    const valueInput = wrapper.querySelector("[data-select-value]");
    const options = Array.from(wrapper.querySelectorAll("[data-value]"));
    const defaultLabel = label.textContent;

    let activeIndex = -1;

    function setActiveOption(index) {
        activeIndex = index;

        options.forEach((option, i) => {
            option.classList.toggle("is-active", i === index);
        });

        if (index === -1) {
            toggle.removeAttribute("aria-activedescendant");
            return;
        }

        toggle.setAttribute("aria-activedescendant", options[index].id);
        options[index].scrollIntoView({ block: "nearest" });
    }

    function close() {
        optionsList.hidden = true;
        wrapper.classList.remove("is-open");
        toggle.setAttribute("aria-expanded", "false");
        setActiveOption(-1);
        document.removeEventListener("click", handleOutsideClick);
    }

    function open() {
        optionsList.hidden = false;
        wrapper.classList.add("is-open");
        toggle.setAttribute("aria-expanded", "true");

        const selectedIndex = options.findIndex((option) => option.getAttribute("aria-selected") === "true");
        setActiveOption(selectedIndex !== -1 ? selectedIndex : 0);
        document.addEventListener("click", handleOutsideClick);
    }

    function handleOutsideClick(event) {
        if (!wrapper.contains(event.target)) {
            close();
            onInteract();
        }
    }

    function selectOption(option) {
        valueInput.value = option.dataset.value;
        label.textContent = option.textContent;
        wrapper.classList.add("has-value");
        options.forEach((item) => item.setAttribute("aria-selected", item === option ? "true" : "false"));
        close();
        toggle.focus();
        onInteract();
    }

    toggle.addEventListener("click", () => {
        if (optionsList.hidden) {
            open();
        } else {
            close();
            onInteract();
        }
    });

    toggle.addEventListener("keydown", (event) => {
        const { key } = event;

        if (optionsList.hidden) {
            if (key === "ArrowDown" || key === "ArrowUp" || key === "Enter" || key === " ") {
                event.preventDefault();
                open();
            }
            return;
        }

        if (key === "ArrowDown") {
            event.preventDefault();
            setActiveOption(Math.min(activeIndex + 1, options.length - 1));
        } else if (key === "ArrowUp") {
            event.preventDefault();
            setActiveOption(Math.max(activeIndex - 1, 0));
        } else if (key === "Home") {
            event.preventDefault();
            setActiveOption(0);
        } else if (key === "End") {
            event.preventDefault();
            setActiveOption(options.length - 1);
        } else if (key === "Enter" || key === " ") {
            event.preventDefault();
            if (activeIndex !== -1) {
                selectOption(options[activeIndex]);
            }
        } else if (key === "Escape") {
            close();
            onInteract();
        } else if (key === "Tab") {
            close();
            onInteract();
        }
    });

    options.forEach((option) => {
        option.addEventListener("click", () => selectOption(option));
        option.addEventListener("mouseenter", () => setActiveOption(options.indexOf(option)));
    });

    return {
        isValid() {
            return Boolean(valueInput.value);
        },
        getControl() {
            return toggle;
        },
        reset() {
            valueInput.value = "";
            label.textContent = defaultLabel;
            wrapper.classList.remove("has-value");
            options.forEach((item) => item.setAttribute("aria-selected", "false"));
        },
    };
}

function initContattiForm(form) {
    const submitButton = form.querySelector(".contatti__submit");
    const messageEl = form.querySelector("[data-contatti-message]");
    const nomeInput = form.querySelector('[name="nome"]');
    // cognome/oggetto esistono solo nel form "Parliamone" dell'archivio
    // Servizi (template-parts/archivio-servizi/cta.php), non nel form
    // Contatti della home: qui sotto sono sempre trattati come opzionali,
    // in modo che questa stessa funzione validi correttamente entrambi.
    const cognomeInput = form.querySelector('[name="cognome"]');
    const emailInput = form.querySelector('[name="email"]');
    const oggettoInput = form.querySelector('[name="oggetto"]');
    const messaggioInput = form.querySelector('[name="messaggio"]');
    const selectWrapper = form.querySelector("[data-contatti-select]");

    const touched = new Set();

    function validate() {
        const nomeValid = NAME_PATTERN.test(nomeInput.value.trim());
        const cognomeValid = !cognomeInput || NAME_PATTERN.test(cognomeInput.value.trim());
        const emailValid = EMAIL_PATTERN.test(emailInput.value.trim());
        const oggettoValid = !oggettoInput || oggettoInput.value.trim().length >= SUBJECT_MIN_LENGTH;
        const messaggioValid = messaggioInput.value.trim().length >= MESSAGE_MIN_LENGTH;
        const motivoValid = selectApi ? selectApi.isValid() : true;

        setFieldState(form, "nome", nomeInput, nomeValid, touched.has("nome"));
        setFieldState(form, "cognome", cognomeInput, cognomeValid, touched.has("cognome"));
        setFieldState(form, "email", emailInput, emailValid, touched.has("email"));
        setFieldState(form, "oggetto", oggettoInput, oggettoValid, touched.has("oggetto"));
        setFieldState(form, "messaggio", messaggioInput, messaggioValid, touched.has("messaggio"));
        setFieldState(form, "motivo", selectApi ? selectApi.getControl() : null, motivoValid, touched.has("motivo"));

        return nomeValid && cognomeValid && emailValid && oggettoValid && messaggioValid && motivoValid;
    }

    // Il submit resta sempre focalizzabile/abilitato (un pulsante disabled è
    // escluso dal tab order: chi naviga da tastiera non lo incontrerebbe mai
    // finché il form non è già valido, senza sapere che esiste). L'invalidità
    // si segnala al submit, non prima, spostando il focus sul primo campo errato.
    function focusFirstInvalid() {
        const firstInvalid = form.querySelector(".is-invalid");

        if (firstInvalid) {
            firstInvalid.focus();
        }
    }

    function markMotivoTouched() {
        touched.add("motivo");
        validate();
    }

    const selectApi = selectWrapper ? initContattiSelect(selectWrapper, markMotivoTouched) : null;

    [
        [nomeInput, "nome"],
        [cognomeInput, "cognome"],
        [emailInput, "email"],
        [oggettoInput, "oggetto"],
        [messaggioInput, "messaggio"],
    ].forEach(([input, name]) => {
        if (!input) {
            return;
        }

        // "touched" già dal primo carattere digitato: la validazione deve
        // essere live durante la scrittura, non solo quando si esce dal campo.
        input.addEventListener("input", () => {
            touched.add(name);
            validate();
        });
        input.addEventListener("blur", () => {
            touched.add(name);
            validate();
        });
    });

    validate();

    form.addEventListener("submit", async (event) => {
        event.preventDefault();

        touched.add("nome");
        touched.add("cognome");
        touched.add("email");
        touched.add("oggetto");
        touched.add("messaggio");
        touched.add("motivo");

        if (!validate()) {
            setMessage(messageEl, "Controlla i campi evidenziati e correggi gli errori indicati.", "error");
            focusFirstInvalid();
            return;
        }

        const settings = (window.Castro && window.Castro.settings) || {};
        const formData = new FormData(form);

        const payload = {
            nome: formData.get("nome") || "",
            email: formData.get("email") || "",
            messaggio: formData.get("messaggio") || "",
            sito_web: formData.get("sito_web") || "",
            nonce: settings.contattiNonce || "",
        };

        if (cognomeInput) {
            payload.cognome = formData.get("cognome") || "";
        }
        if (oggettoInput) {
            payload.oggetto = formData.get("oggetto") || "";
        }
        if (selectApi) {
            payload.motivo = formData.get("motivo") || "";
        }
        if (form.querySelector('[name="sorgente"]')) {
            payload.sorgente = formData.get("sorgente") || "";
        }

        submitButton.disabled = true;
        submitButton.classList.add("is-loading");
        setMessage(messageEl, "", null);

        try {
            const response = await fetch(`${settings.api_url}castro/v1/contatti`, {
                method: "POST",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setMessage(messageEl, data.message, "success");
                form.reset();
                touched.clear();
                if (selectApi) {
                    selectApi.reset();
                }
                validate();
            } else {
                setMessage(messageEl, data.message || "Si è verificato un errore, riprova più tardi.", "error");
                validate();
            }
        } catch (error) {
            setMessage(messageEl, "Impossibile inviare la richiesta, controlla la connessione e riprova.", "error");
            validate();
        } finally {
            // Il disabled qui serve solo a impedire un doppio invio mentre la
            // richiesta è in corso: va sempre rimosso a fine richiesta, non
            // lasciato al risultato della validazione (vedi validate()).
            submitButton.disabled = false;
            submitButton.classList.remove("is-loading");
        }
    });
}

export function initContatti() {
    document.querySelectorAll("[data-contatti-form]").forEach(initContattiForm);
}
